import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  GROUPED_GUIDED_PANELS,
  groupedImpactTrackingLocked,
  groupedGuidedPanelDisplayStatus,
  groupedGuidedPanelPrimaryActionLabel,
  groupedGuidedPanelShowsPrimaryAction,
  groupedGuidedPanelShowsReviewedFooter,
  groupedGuidedPanelStatusLabel,
  groupedGuidedReportPanelStatus,
  type GroupedGuidedPanelId,
  type GroupedGuidedPanelStatusContext,
} from '../../domain/incidents/groupedGuidedWorkflow';
import { representativeAuditScopeBanner } from '../../domain/incidents/groupedRepresentativeAuditCopy';
import {
  type SafetyWorkflowProgress,
  type SafetyWorkflowStepId,
} from '../../domain/safetyWorkflowStepMapping';
import {
  groupedWorkflowStepperStatusCaption,
} from '../../domain/incidents/groupedSafetyCaseStepLabels';
import {
  groupedCorrectiveActionPanelDescriptionUx,
  groupedImpactTrackingPanelDescriptionUx,
  groupedSafetyReportPanelDescriptionUx,
  groupedVerifyEvidencePanelDescription,
  groupedWorkflowStepperStatusHint,
} from '../../domain/incidents/groupedReviewWorkflowUx';
import SafetyWorkflowStepper from '../safety/SafetyWorkflowStepper';
import {
  patternOwnedWorkflowIntro,
  resolvePatternOwnedPanelIds,
  resolvePatternOwnedWorkflowSteps,
} from '../../domain/incidents/patternOwnershipContainment';

export type GroupedGuidedWorkflowPanels = Partial<Record<GroupedGuidedPanelId, React.ReactNode>>;

export type GroupedGuidedPanelExtras = Partial<Record<GroupedGuidedPanelId, React.ReactNode>>;

export type GroupedIncidentGuidedWorkflowProps = {
  progress: SafetyWorkflowProgress;
  relatedCount?: number;
  representativeIncidentId?: string;
  reportBlocked?: boolean;
  reportBlockedReason?: string;
  signOffMissingItems?: string[];
  panels: GroupedGuidedWorkflowPanels;
  panelExtras?: GroupedGuidedPanelExtras;
  advancedDetails?: React.ReactNode;
  onPrimaryAction?: (panelId: GroupedGuidedPanelId) => void;
  onSecondaryAction?: (panelId: GroupedGuidedPanelId) => void;
  onRegisterPanelControl?: (control: { expand: (panelId: GroupedGuidedPanelId) => void }) => void;
  initialPanelId?: GroupedGuidedPanelId;
  panelStatusContext?: GroupedGuidedPanelStatusContext;
  /** Weak browser-only hint; not workflow completion. */
  localProgressHint?: string | null;
  onClearLocalProgress?: () => void;
  onExpandedPanelChange?: (panelId: GroupedGuidedPanelId | false) => void;
  /** When true, Pattern CA/Impact/Report are not Pattern-owned lifecycle stages. */
  hasLinkedSafetyCase?: boolean;
};

export const GroupedIncidentGuidedWorkflow: React.FC<GroupedIncidentGuidedWorkflowProps> = ({
  progress,
  relatedCount,
  representativeIncidentId,
  reportBlocked = false,
  reportBlockedReason,
  signOffMissingItems = [],
  panels,
  panelExtras,
  advancedDetails,
  onPrimaryAction,
  onSecondaryAction: _onSecondaryAction,
  onRegisterPanelControl,
  initialPanelId,
  panelStatusContext,
  localProgressHint,
  onClearLocalProgress,
  onExpandedPanelChange,
  hasLinkedSafetyCase = false,
}) => {
  void _onSecondaryAction;
  const [expandedPanel, setExpandedPanel] = useState<GroupedGuidedPanelId | false>(
    initialPanelId ?? false,
  );
  const userExpandedPanelRef = React.useRef(false);

  const setExpandedPanelState = React.useCallback(
    (next: GroupedGuidedPanelId | false) => {
      if (next) userExpandedPanelRef.current = true;
      setExpandedPanel(next);
      onExpandedPanelChange?.(next);
    },
    [onExpandedPanelChange],
  );

  useEffect(() => {
    userExpandedPanelRef.current = false;
  }, [representativeIncidentId]);

  useEffect(() => {
    if (!initialPanelId || userExpandedPanelRef.current) return;
    setExpandedPanelState(initialPanelId);
  }, [initialPanelId, representativeIncidentId, setExpandedPanelState]);

  useEffect(() => {
    onRegisterPanelControl?.({
      expand: (panelId) => setExpandedPanelState(panelId),
    });
  }, [onRegisterPanelControl, setExpandedPanelState]);

  const correctiveActionLocked = Boolean(panelStatusContext?.correctiveActionLocked);
  const assignActionComplete = progress.completedStepIds.includes('assign_action');
  const impactTrackingLocked = groupedImpactTrackingLocked({
    correctiveActionLocked,
    assignActionComplete,
  });

  const panelStatusContextEnriched = useMemo(
    () => ({
      ...panelStatusContext,
      recurringRiskIdentified: progress.completedStepIds.includes('detected'),
      hasLinkedSafetyCase,
    }),
    [panelStatusContext, progress.completedStepIds, hasLinkedSafetyCase],
  );

  const visiblePanelIds = useMemo(
    () => new Set(resolvePatternOwnedPanelIds(hasLinkedSafetyCase)),
    [hasLinkedSafetyCase],
  );
  const ownershipSteps = useMemo(
    () => resolvePatternOwnedWorkflowSteps(hasLinkedSafetyCase),
    [hasLinkedSafetyCase],
  );
  const workflowIntro = patternOwnedWorkflowIntro(hasLinkedSafetyCase);

  const lockedStepIds = useMemo<SafetyWorkflowStepId[]>(() => [], []);
  const blockedStepIds = useMemo<SafetyWorkflowStepId[]>(() => [], []);

  return (
    <Box data-testid="grouped-guided-workflow">
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          borderColor: '#d0d7e2',
          bgcolor: '#fbfcfe',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
          Safety workflow
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25, lineHeight: 1.55 }}>
          {workflowIntro}
        </Typography>
        <SafetyWorkflowStepper
          progress={progress}
          steps={ownershipSteps}
          blockedStepIds={blockedStepIds}
          lockedStepIds={lockedStepIds}
          showStatusLabels
          showNextAction
          statusCaption={(stepId, status) =>
            groupedWorkflowStepperStatusCaption(
              stepId,
              status,
              panelStatusContext?.correctiveWorkflowPhase,
            )}
          statusHint={(stepId, status) => groupedWorkflowStepperStatusHint(stepId, status)}
        />
      </Paper>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} data-testid="grouped-guided-scope-banner">
        {representativeAuditScopeBanner(relatedCount, representativeIncidentId)}
      </Alert>

      {localProgressHint ? (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 2, borderRadius: 2 }}
          data-testid="grouped-guided-local-progress-hint"
          action={
            onClearLocalProgress ? (
              <Button color="inherit" size="small" onClick={onClearLocalProgress}>
                Clear
              </Button>
            ) : undefined
          }
        >
          {localProgressHint}
        </Alert>
      ) : null}

      {GROUPED_GUIDED_PANELS.map((panel) => {
        const panelContent = panels[panel.id];
        if (!panelContent) return null;
        if (!visiblePanelIds.has(panel.id)) return null;
        const panelLabel = hasLinkedSafetyCase && panel.id === 'decide_action'
          ? 'Managed in Safety Case'
          : panel.label;
        const status = hasLinkedSafetyCase && panel.id === 'decide_action'
          ? 'current'
          : groupedGuidedPanelDisplayStatus(panel.id, progress, {
            blockedStepIds,
            lockedStepIds,
            correctiveActionLocked,
            correctiveWorkflowPhase: panelStatusContext?.correctiveWorkflowPhase,
          });
        const expanded = expandedPanel === panel.id;
        const statusLabel = hasLinkedSafetyCase && panel.id === 'decide_action'
          ? 'Open'
          : groupedGuidedPanelStatusLabel(panel.id, status, panelStatusContextEnriched);
        const panelDescription = panel.id === 'verify_evidence'
          ? groupedVerifyEvidencePanelDescription(status, panelStatusContext?.evidenceConfirmed)
          : panel.id === 'decide_action' && hasLinkedSafetyCase
            ? 'Record what was done on the Safety Case. Investigate stays on evidence and pattern.'
          : panel.id === 'decide_action'
            ? groupedCorrectiveActionPanelDescriptionUx(correctiveActionLocked, status)
            : panel.id === 'track_impact'
              ? groupedImpactTrackingPanelDescriptionUx(impactTrackingLocked)
              : panel.id === 'report'
                ? groupedSafetyReportPanelDescriptionUx(
                  Boolean(panelStatusContext?.reportSignOffBlocked ?? reportBlocked),
                )
                : panel.description;
        const reportStatusDetail = panel.id === 'report'
          ? groupedGuidedReportPanelStatus(panelStatusContext)
          : null;
        const isReportPanel = panel.id === 'report';
        const statusColor = reportStatusDetail?.signOffLabel
          ? 'warning.main'
          : status === 'done'
            ? 'success.main'
            : status === 'current'
              ? 'primary.main'
              : status === 'blocked' || statusLabel === 'Pending required steps'
                ? 'warning.main'
                : status === 'locked' || statusLabel === 'Locked'
                  ? 'text.secondary'
                : statusLabel === 'Ready' || statusLabel === 'Assigned' || statusLabel === 'In progress'
                  ? 'info.main'
                  : panel.id === 'decide_action' && statusLabel === 'Completed'
                    ? 'success.main'
                    : statusLabel === 'Draft ready'
                      ? 'success.main'
                      : 'text.secondary';

        return (
          <Accordion
            key={panel.id}
            expanded={expanded}
            onChange={(_, nextExpanded) => setExpandedPanelState(nextExpanded ? panel.id : false)}
            disableGutters
            data-testid={`grouped-guided-panel-${panel.id}`}
            sx={{
              mb: 1.25,
              borderRadius: '12px !important',
              border: '1px solid #d0d7e2',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{panelLabel}</Typography>
                  <Typography
                    variant="caption"
                    data-testid={`grouped-guided-panel-status-${panel.id}`}
                    sx={{
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: 0.45,
                      color: statusColor,
                    }}
                  >
                    {statusLabel}
                  </Typography>
                  {reportStatusDetail?.signOffLabel ? (
                    <Typography
                      variant="caption"
                      data-testid="grouped-guided-panel-report-signoff-status"
                      sx={{
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: 0.45,
                        color: 'warning.main',
                      }}
                    >
                      {reportStatusDetail.signOffLabel}
                    </Typography>
                  ) : null}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.5 }}>
                  {panelDescription}
                </Typography>
                {status === 'locked' && panel.id === 'track_impact' ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid="grouped-guided-panel-lock-hint-track_impact"
                    sx={{ mt: 0.35, display: 'block', lineHeight: 1.45, fontWeight: 600 }}
                  >
                    Locked until corrective follow-up has started.
                  </Typography>
                ) : null}
                {panel.id === 'report' && (status === 'blocked' || statusLabel === 'Pending required steps') ? (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    data-testid="grouped-guided-panel-block-hint-report"
                    sx={{ mt: 0.35, display: 'block', lineHeight: 1.45, fontWeight: 600 }}
                  >
                  Pending corrective action and impact measurement.
                  </Typography>
                ) : null}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              {isReportPanel && reportStatusDetail ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
                  <Typography
                    variant="body2"
                    data-testid="grouped-report-draft-status"
                    sx={{ fontWeight: 800, color: reportStatusDetail.signOffLabel ? 'success.main' : 'warning.main' }}
                  >
                    {reportStatusDetail.draftLabel}
                  </Typography>
                  {reportStatusDetail.signOffLabel ? (
                    <Typography
                      variant="body2"
                      data-testid="grouped-report-signoff-status"
                      sx={{ fontWeight: 800, color: 'warning.main' }}
                    >
                      {reportStatusDetail.signOffLabel}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
              {isReportPanel && reportBlocked && reportBlockedReason ? (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }} data-testid="grouped-report-blocked">
                  {reportBlockedReason}
                </Alert>
              ) : null}
              {isReportPanel && signOffMissingItems.length > 0 ? (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Missing for final safety report sign-off
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.25 }} spacing={0.35}>
                    {signOffMissingItems.map((item) => (
                      <Typography key={item} component="li" variant="body2" color="text.secondary">
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ) : null}
              {panelContent}
              {panelExtras?.[panel.id] ? (
                <Box sx={{ mt: 1.5 }}>{panelExtras[panel.id]}</Box>
              ) : null}
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {groupedGuidedPanelShowsReviewedFooter(panel.id, status, panelStatusContext) ? (
                  <Typography
                    variant="body2"
                    data-testid={`grouped-guided-panel-reviewed-${panel.id}`}
                    sx={{ fontWeight: 800, color: 'success.main' }}
                  >
                    Reviewed
                  </Typography>
                ) : groupedGuidedPanelShowsPrimaryAction(panel.id, status, panelStatusContextEnriched) ? (
                  <>
                    <Button
                      variant="contained"
                      onClick={(event) => {
                        event.stopPropagation();
                        onPrimaryAction?.(panel.id);
                      }}
                      sx={{ fontWeight: 800 }}
                    >
                      {groupedGuidedPanelPrimaryActionLabel(panel.id, panel.primaryActionLabel, panelStatusContextEnriched)}
                    </Button>
                  </>
                ) : null}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {advancedDetails ? (
        <Box data-testid="grouped-guided-advanced" sx={{ mt: 1 }}>
          {advancedDetails}
        </Box>
      ) : null}
    </Box>
  );
};

export default GroupedIncidentGuidedWorkflow;
